import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { motion } from "framer-motion"
import {
	Alert,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
} from "@mui/material"
import { MdEvent, MdPayments, MdCampaign, MdExpandMore, MdHelpOutline, MdClose } from "react-icons/md"

import TopoMenu from "../../../componentes/gerais/TopoMenu"
import MenuLateral from "../../../componentes/gerais/Menu"
import { Spinner } from "../../../componentes/Spinner"
import Refresher from "../../../componentes/uteis/Refresher"
import { configAnim } from "../../../servicos/config/configAnim"
import { ContextoGeral } from "../../../contextos/ContextoGeral"
import ServicoAPI from "../../../servicos/ServicoAPI"
import MensagemApp from "../../../servicos/config/MensagemApp"
import "./campanhas.css"

function classeStatus(status) {
	if (status === "pago") return "camp-card--pago"
	if (status === "concluida") return "camp-card--concluida"
	if (status === "encerrada") return "camp-card--encerrada"
	return ""
}

function rotuloFrequenciaMeta(frequencia, frequenciaLabel) {
	const mapa = {
		diaria: "diário",
		semanal: "semanal",
		quinzenal: "quinzenal",
		mensal: "mensal",
	}
	const f = String(frequencia || "").toLowerCase().trim()
	if (mapa[f]) return mapa[f]
	const lbl = String(frequenciaLabel || "").toLowerCase().trim()
	if (lbl.startsWith("diár") || lbl.startsWith("diar")) return "diário"
	if (lbl) return lbl
	return "mensal"
}

function textoMetaCampanha(item) {
	const qtde = Number(item?.qtdeServicos) || 0
	const servico = qtde === 1 ? "serviço" : "serviços"
	const freq = rotuloFrequenciaMeta(item?.frequencia, item?.frequenciaLabel)
	return `Meta: ${qtde} ${servico} (${freq})`
}

function textoPrevisaoPagamento(item) {
	if (item?.status === "pago" && item?.pagoEmTexto) {
		return `Pago em: ${item.pagoEmTexto}`
	}
	// Só mostra previsão depois de bater a meta (concluída aguardando ciclo fechar / pagamento).
	const bateuMeta =
		item?.status === "concluida"
		|| Number(item?.percentual) >= 100
		|| (Number(item?.qtdeRestante) === 0 && Number(item?.qtdeRealizados) > 0)
	if (bateuMeta && item?.previsaoPagamentoTexto) {
		return `Bonificação prevista para ${item.previsaoPagamentoTexto}`
	}
	return ""
}

function dataHojeIso() {
	const d = new Date()
	const y = d.getFullYear()
	const m = String(d.getMonth() + 1).padStart(2, "0")
	const day = String(d.getDate()).padStart(2, "0")
	return `${y}-${m}-${day}`
}

const LIMITE_CATEGORIAS_REGRAS = 3

function parseRegrasTexto(regras) {
	const texto = String(regras || "")
	const ini = " na(s) categoria(s) "
	const fim = " no ciclo atual"
	const i = texto.indexOf(ini)
	if (i === -1) return { prefixo: texto, sufixo: "" }
	const j = texto.indexOf(fim, i)
	if (j === -1) return { prefixo: texto, sufixo: "" }
	return {
		prefixo: texto.slice(0, i),
		sufixo: texto.slice(j),
	}
}

function listarCategorias(categoriasLabel) {
	const lbl = String(categoriasLabel || "").trim()
	if (!lbl || lbl === "Todas as categorias") return []
	return lbl.split(",").map((c) => c.trim()).filter(Boolean)
}

function BlocoRegras({ regras, categoriasLabel }) {
	const [expandido, setExpandido] = useState(false)
	const categorias = useMemo(() => listarCategorias(categoriasLabel), [categoriasLabel])
	const { prefixo, sufixo } = useMemo(() => parseRegrasTexto(regras), [regras])
	const temMuitas = categorias.length > LIMITE_CATEGORIAS_REGRAS
	const visiveis = expandido || !temMuitas
		? categorias
		: categorias.slice(0, LIMITE_CATEGORIAS_REGRAS)
	const ocultas = temMuitas && !expandido ? categorias.length - LIMITE_CATEGORIAS_REGRAS : 0

	if (!categorias.length) {
		return (
			<div className="camp-card__regras">
				<strong>Regras</strong>
				<p className="camp-card__regras-texto">{regras}</p>
			</div>
		)
	}

	return (
		<div className="camp-card__regras">
			<strong>Regras</strong>
			<p className="camp-card__regras-texto">
				{prefixo}
				{" na(s) categoria(s):"}
			</p>
			<div className={`camp-card__categorias${expandido ? " camp-card__categorias--aberto" : ""}`}>
				{visiveis.map((cat) => (
					<span key={cat} className="camp-card__categoria-tag">{cat}</span>
				))}
				{ocultas > 0 ? (
					<span className="camp-card__categoria-tag camp-card__categoria-tag--mais">
						+{ocultas}
					</span>
				) : null}
			</div>
			<p className="camp-card__regras-texto camp-card__regras-texto--fim">{sufixo}</p>
			{temMuitas ? (
				<button
					type="button"
					className="camp-card__regras-toggle"
					aria-expanded={expandido}
					onClick={() => setExpandido((v) => !v)}
				>
					{expandido ? "Ver menos categorias" : `Ver todas (${categorias.length})`}
					<MdExpandMore size={18} aria-hidden />
				</button>
			) : null}
		</div>
	)
}

function CardCampanhaAtiva({ item }) {
	const [detalhesAberto, setDetalhesAberto] = useState(false)
	const previsaoTexto = textoPrevisaoPagamento(item)

	return (
		<article className={`camp-card ${classeStatus(item.status)}`}>
			<div className="camp-card__head">
				<div className="camp-card__ident">
					<h2 className="camp-card__titulo">{item.nome}</h2>
					{String(item.descricao || "").trim() ? (
						<p className="camp-card__descricao">{item.descricao}</p>
					) : null}
				</div>
				<span className={`camp-card__status camp-card__status--${item.status}`}>
					{item.statusLabel}
				</span>
				<p className="camp-card__periodo camp-card__periodo--head">
					<MdEvent size={16} aria-hidden />
					<span>Vigência: {item.vigenciaTexto}</span>
				</p>
				<div className={`camp-card__bonus camp-card__bonus--compacto${item.status === "pago" ? " camp-card__bonus--pago" : ""}${previsaoTexto ? " camp-card__bonus--com-previsao" : ""}`}>
					<div className="camp-card__bonus-topo">
						<span>{item.status === "pago" ? "Bonificação paga" : "Bonificação prevista"}</span>
						<strong>{item.valorBonusFormatado}</strong>
					</div>
					{previsaoTexto ? (
						<small className="camp-card__bonus-previsao">
							{previsaoTexto}
							{item.status !== "pago" && item.cicloTexto ? (
								<span> · ciclo {item.cicloTexto}</span>
							) : null}
						</small>
					) : null}
				</div>
				<p className="camp-card__meta camp-card__meta--head">{textoMetaCampanha(item)}</p>
			</div>

			<div className="camp-card__progresso">
				<div className="camp-card__progresso-topo">
					<span className="camp-card__progresso-label">Progresso</span>
					<span className="camp-card__percentual">{item.percentual}%</span>
				</div>
				<div
					className="camp-card__barra"
					role="progressbar"
					aria-valuenow={item.percentual}
					aria-valuemin={0}
					aria-valuemax={100}
				>
					<div
						className="camp-card__barra-fill"
						style={{ width: `${Math.min(100, Math.max(0, Number(item.percentual) || 0))}%` }}
					/>
				</div>
			</div>

			<button
				type="button"
				className={`camp-card__toggle${detalhesAberto ? " camp-card__toggle--aberto" : ""}`}
				aria-expanded={detalhesAberto}
				onClick={() => setDetalhesAberto((v) => !v)}
			>
				<span>{detalhesAberto ? "Ocultar detalhes" : "Ver detalhes"}</span>
				<MdExpandMore size={22} aria-hidden />
			</button>

			{detalhesAberto ? (
				<div className="camp-card__expandido">
					<BlocoRegras regras={item.regras} categoriasLabel={item.categoriasLabel} />

					<dl className="camp-card__fatos">
						<div className="camp-card__fato">
							<span>Serviços realizados</span>
							<strong>{item.qtdeRealizados} / {item.qtdeServicos}</strong>
						</div>
						<div className="camp-card__fato">
							<span>Restante para a meta</span>
							<strong>{item.qtdeRestante}</strong>
						</div>
						<div className="camp-card__fato">
							<span>Status</span>
							<strong>{item.statusLabel}</strong>
						</div>
					</dl>
				</div>
			) : null}
		</article>
	)
}

function CardPagamento({ item }) {
	const [detalhesAberto, setDetalhesAberto] = useState(false)

	return (
		<article className="camp-card camp-card--pago">
			<div className="camp-card__head">
				<div className="camp-card__ident">
					<h2 className="camp-card__titulo">{item.nome}</h2>
					{String(item.descricao || "").trim() ? (
						<p className="camp-card__descricao">{item.descricao}</p>
					) : null}
				</div>
				<span className="camp-card__status camp-card__status--pago">
					{item.statusLabel || "Pago"}
				</span>
				<p className="camp-card__periodo camp-card__periodo--head">
					<MdEvent size={16} aria-hidden />
					<span>
						Pago em: {item.pagoEmTexto || "—"}
						{item.cicloTexto ? ` · Ciclo: ${item.cicloTexto}` : ""}
					</span>
				</p>
				<div className="camp-card__bonus camp-card__bonus--compacto camp-card__bonus--pago">
					<span>Valor pago</span>
					<strong>{item.valorBonusFormatado}</strong>
				</div>
				{item.frequenciaLabel ? (
					<p className="camp-card__meta camp-card__meta--head">Frequência: {item.frequenciaLabel}</p>
				) : null}
			</div>


			<button
				type="button"
				className={`camp-card__toggle${detalhesAberto ? " camp-card__toggle--aberto" : ""}`}
				aria-expanded={detalhesAberto}
				onClick={() => setDetalhesAberto((v) => !v)}
			>
				<span>{detalhesAberto ? "Ocultar detalhes" : "Ver detalhes"}</span>
				<MdExpandMore size={22} aria-hidden />
			</button>

			{detalhesAberto ? (
				<dl className="camp-card__fatos">
					<div className="camp-card__fato">
						<span>Serviços do ciclo</span>
						<strong>{item.qtdeRealizada} / {item.qtdeMeta}</strong>
					</div>
				</dl>
			) : null}
		</article>
	)
}

function ModalAjudaCampanhas({ aberto, onFechar }) {
	return (
		<Dialog
			open={aberto}
			onClose={onFechar}
			fullWidth
			maxWidth="sm"
			aria-labelledby="camp-ajuda-titulo"
		>
			<DialogTitle id="camp-ajuda-titulo" className="camp-ajuda__titulo">
				<span>Como funcionam as metas</span>
				<IconButton
					type="button"
					aria-label="Fechar"
					onClick={onFechar}
					size="small"
					edge="end"
				>
					<MdClose size={22} />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers className="camp-ajuda__conteudo">
				<p>
					Cada meta define uma quantidade de <strong>serviços finalizados</strong> em um período
					(diário, semanal, quinzenal ou mensal) e um valor de bônus se você atingir o objetivo.
				</p>
				<ul>
					<li>
						<strong>Só contam serviços finalizados.</strong> Serviços cancelados ou em
						andamento não entram na meta.
					</li>
					<li>
						Se a meta tiver <strong>categorias</strong> (ex.: Mototaxi), só esses tipos
						contam.
					</li>
					<li>
						O progresso é do <strong>ciclo atual</strong>. Ao fechar o ciclo, a contagem
						reinicia no próximo (enquanto a meta estiver vigente).
					</li>
					<li>
						<strong>Bater a meta cedo não libera o pagamento na hora.</strong> O bônus
						só é creditado depois que o ciclo termina — em geral no dia seguinte ao fim
						do ciclo.
					</li>
					<li>
						Quando você completar a meta, o card mostra a{" "}
						<strong>data prevista de pagamento</strong> e o período do ciclo.
					</li>
					<li>
						A <strong>vigência</strong> limita o ciclo. Se houver data final, o último dia
						entra na contagem. Sem data final, a meta segue até a empresa desativar.
						Exemplo: meta semanal vigente até 30/09 — mesmo que a semana “natural” fosse
						depois, o ciclo fecha em 30/09 e a previsão de pagamento fica para 01/10.
					</li>
				</ul>
				<p className="camp-ajuda__nota">
					A data de pagamento é uma previsão: o crédito ocorre após a rotina diária
					processar os ciclos já encerrados.
				</p>
			</DialogContent>
			<DialogActions>
				<Button onClick={onFechar} variant="contained" color="primary">
					Entendi
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default function Campanhas() {
	const { dados } = useContext(ContextoGeral)
	const [menu, setMenu] = useState(false)
	const [aba, setAba] = useState("ativas")
	const [ajudaAberta, setAjudaAberta] = useState(false)

	const [carregandoAtivas, setCarregandoAtivas] = useState(true)
	const [erroAtivas, setErroAtivas] = useState(false)
	const [listaAtivas, setListaAtivas] = useState([])

	const dataPadrao = useMemo(() => dataHojeIso(), [])
	const [dataInicio, setDataInicio] = useState(dataPadrao)
	const [dataFim, setDataFim] = useState(dataPadrao)
	const [filtroInicio, setFiltroInicio] = useState(dataPadrao)
	const [filtroFim, setFiltroFim] = useState(dataPadrao)

	const [carregandoPagamentos, setCarregandoPagamentos] = useState(false)
	const [erroPagamentos, setErroPagamentos] = useState(false)
	const [listaPagamentos, setListaPagamentos] = useState([])
	const [totalPagamentos, setTotalPagamentos] = useState("R$ 0,00")
	const [pagamentosCarregados, setPagamentosCarregados] = useState(false)

	const carregarAtivas = useCallback(async () => {
		if (!dados?.dominio || !dados?.idProf || !dados?.token) return
		setCarregandoAtivas(true)
		try {
			const resposta = await ServicoAPI.campanhasBonificacaoApp(dados)
			if (resposta?.resultado) {
				setListaAtivas(Array.isArray(resposta.dados?.lista) ? resposta.dados.lista : [])
				setErroAtivas(false)
			} else {
				setListaAtivas([])
				setErroAtivas(true)
			}
		} catch {
			setListaAtivas([])
			setErroAtivas(true)
		} finally {
			setCarregandoAtivas(false)
		}
	}, [dados?.dominio, dados?.idProf, dados?.token])

	const carregarPagamentos = useCallback(async (inicio, fim) => {
		if (!dados?.dominio || !dados?.idProf || !dados?.token) return
		setCarregandoPagamentos(true)
		try {
			const resposta = await ServicoAPI.campanhasBonificacaoPagamentosApp(dados, {
				dataInicio: inicio,
				dataFim: fim,
			})
			if (resposta?.resultado) {
				setListaPagamentos(Array.isArray(resposta.dados?.lista) ? resposta.dados.lista : [])
				setTotalPagamentos(resposta.dados?.totalValorFormatado || "R$ 0,00")
				if (resposta.dados?.dataInicio) setFiltroInicio(resposta.dados.dataInicio)
				if (resposta.dados?.dataFim) setFiltroFim(resposta.dados.dataFim)
				setErroPagamentos(false)
			} else {
				setListaPagamentos([])
				setTotalPagamentos("R$ 0,00")
				setErroPagamentos(true)
			}
		} catch {
			setListaPagamentos([])
			setTotalPagamentos("R$ 0,00")
			setErroPagamentos(true)
		} finally {
			setCarregandoPagamentos(false)
			setPagamentosCarregados(true)
		}
	}, [dados?.dominio, dados?.idProf, dados?.token])

	useEffect(() => {
		carregarAtivas()
	}, [carregarAtivas])

	useEffect(() => {
		if (aba !== "pagamentos") return
		if (pagamentosCarregados) return
		carregarPagamentos(dataInicio, dataFim)
	}, [aba, pagamentosCarregados, carregarPagamentos, dataInicio, dataFim])

	const buscarHistorico = () => {
		const ini = dataInicio || dataPadrao
		const fim = dataFim || dataPadrao
		setDataInicio(ini)
		setDataFim(fim)
		carregarPagamentos(ini, fim)
	}

	return (
		<motion.div
			className="camp-app"
			style={{ height: "100%" }}
			initial={configAnim.initial}
			animate={configAnim.animate}
			exit={configAnim.exit}
			transition={configAnim.transition}
		>
			<TopoMenu voltar="/app/home" titulo="METAS" setShow={setMenu} />
			<div className="camp-app__conteudo">
				<div className="camp-app__topo-ajuda">
					<div className="camp-app__abas" role="tablist" aria-label="Seções de metas">
						<button
							type="button"
							role="tab"
							aria-selected={aba === "ativas"}
							className={`camp-app__aba${aba === "ativas" ? " camp-app__aba--ativa" : ""}`}
							onClick={() => setAba("ativas")}
						>
							<MdCampaign size={18} aria-hidden />
							Metas ativas
						</button>
						<button
							type="button"
							role="tab"
							aria-selected={aba === "pagamentos"}
							className={`camp-app__aba${aba === "pagamentos" ? " camp-app__aba--ativa" : ""}`}
							onClick={() => setAba("pagamentos")}
						>
							<MdPayments size={18} aria-hidden />
							Pagamentos
						</button>
					</div>
					<button
						type="button"
						className="camp-app__ajuda"
						aria-label="Como funcionam as metas"
						title="Como funcionam as metas"
						onClick={() => setAjudaAberta(true)}
					>
						<MdHelpOutline size={22} aria-hidden />
					</button>
				</div>

				{aba === "ativas" ? (
					<>
						<p className="camp-app__intro">
							Acompanhe as metas disponíveis, o progresso e o bônus previsto.
						</p>

						{carregandoAtivas ? (
							<div className="camp-app__loading">
								<Spinner />
							</div>
						) : erroAtivas ? (
							<div className="camp-app__erro">
								<Refresher onClick={carregarAtivas} />
								<Alert severity="error" className="text-center mt-4">
									{MensagemApp.msgErroApi}
								</Alert>
							</div>
						) : listaAtivas.length === 0 ? (
							<p className="camp-app__vazio">Nenhuma meta disponível no momento.</p>
						) : (
							<div className="camp-app__lista">
								{listaAtivas.map((item) => (
									<CardCampanhaAtiva key={item.id} item={item} />
								))}
							</div>
						)}
					</>
				) : (
					<>
						<p className="camp-app__intro">
							Consulte os bônus de meta já creditados. Por padrão mostramos o dia de hoje.
						</p>

						<div className="camp-app__filtro">
							<label className="camp-app__filtro-campo">
								<span>De</span>
								<input
									type="date"
									value={dataInicio}
									max={dataFim || undefined}
									onChange={(e) => setDataInicio(e.target.value)}
								/>
							</label>
							<label className="camp-app__filtro-campo">
								<span>Até</span>
								<input
									type="date"
									value={dataFim}
									min={dataInicio || undefined}
									onChange={(e) => setDataFim(e.target.value)}
								/>
							</label>
							<button
								type="button"
								className="camp-app__filtro-btn"
								onClick={buscarHistorico}
								disabled={carregandoPagamentos}
							>
								Buscar
							</button>
						</div>

						{!carregandoPagamentos && !erroPagamentos ? (
							<div className="camp-app__resumo-pag">
								<span>
									Período: {filtroInicio.split("-").reverse().join("/") || "—"}
									{" a "}
									{filtroFim.split("-").reverse().join("/") || "—"}
								</span>
								<strong className="camp-app__resumo-valor">{totalPagamentos}</strong>
							</div>
						) : null}

						{carregandoPagamentos ? (
							<div className="camp-app__loading">
								<Spinner />
							</div>
						) : erroPagamentos ? (
							<div className="camp-app__erro">
								<button
									type="button"
									className="camp-app__filtro-btn"
									onClick={buscarHistorico}
								>
									Tentar novamente
								</button>
								<Alert severity="error" className="text-center mt-4">
									{MensagemApp.msgErroApi}
								</Alert>
							</div>
						) : listaPagamentos.length === 0 ? (
							<p className="camp-app__vazio">
								Nenhum pagamento de meta neste período.
							</p>
						) : (
							<div className="camp-app__lista">
								{listaPagamentos.map((item) => (
									<CardPagamento key={item.id} item={item} />
								))}
							</div>
						)}
					</>
				)}
			</div>
			<ModalAjudaCampanhas aberto={ajudaAberta} onFechar={() => setAjudaAberta(false)} />
			<MenuLateral show={menu} setShow={setMenu} ativoRota="/app/campanhas" />
		</motion.div>
	)
}
